import { useState } from 'react';
import {
  berekenDuurInFrames,
  randomUUID,
  secondeNaarFrame,
  BEELDFORMATEN,
  type BeeldformaatIdModel,
  type ProjectModel,
} from '@video-studio/shared';
import type { useEditor } from '../../state/useEditor';

/**
 * Templates en huisstijl.
 *
 * Een template is een blauwdruk van het projectmodel ZONDER media: alleen
 * sporen, tekstlagen en instellingen. Daardoor kan een template nooit naar een
 * mediabestand van iemand anders verwijzen.
 */
export interface TemplatePaneelProps {
  editor: ReturnType<typeof useEditor>;
}

interface TemplateDefinitie {
  id: string;
  naam: string;
  beschrijving: string;
  formaat: BeeldformaatIdModel;
  fps: number;
  bouw: (project: ProjectModel) => ProjectModel;
}

/** Standaard huisstijlkleuren; later te overschrijven met eigen waarden. */
const HUISSTIJL = {
  primair: '#e8590c',
  donker: '#111418',
  licht: '#ffffff',
};

function metTekstlaag(
  project: ProjectModel,
  laag: {
    tekst: string;
    startSeconde: number;
    duurSeconde: number;
    y: number;
    fontSize: number;
    fontWeight: number;
    kleur: string;
    animatie?: 'geen' | 'vervagen' | 'omhoog';
  },
): ProjectModel {
  const start = secondeNaarFrame(laag.startSeconde, project.fps);
  const duur = secondeNaarFrame(laag.duurSeconde, project.fps);
  return {
    ...project,
    tracks: project.tracks.map((track) =>
      track.type === 'tekst'
        ? {
            ...track,
            items: [
              ...track.items,
              {
                id: randomUUID(),
                soort: 'tekst' as const,
                naam: laag.tekst.slice(0, 40),
                startFrame: start,
                duurInFrames: duur,
                vergrendeld: false,
                verborgen: false,
                tekst: laag.tekst,
                x: 0.5,
                y: laag.y,
                fontFamily: 'Inter',
                fontSize: laag.fontSize,
                fontWeight: laag.fontWeight,
                kleur: laag.kleur,
                uitlijning: 'midden' as const,
                schaduw: true,
                achtergrondKleur: 'transparant' as const,
                maxBreedte: 0.85,
                dekking: 1,
                rotatie: 0,
                animatie: laag.animatie ?? 'geen',
              },
            ],
          }
        : track,
    ),
  };
}

const TEMPLATES: TemplateDefinitie[] = [
  {
    id: 'productvideo',
    naam: 'Productvideo',
    beschrijving: 'Openingskaart, productnaam en een afsluitende regel met prijs en logo.',
    formaat: '16:9',
    fps: 30,
    bouw: (project) => {
      let volgend = metTekstlaag(project, {
        tekst: 'Nieuw bij DutchHeating',
        startSeconde: 0,
        duurSeconde: 3,
        y: 0.5,
        fontSize: 84,
        fontWeight: 800,
        kleur: HUISSTIJL.licht,
        animatie: 'omhoog',
      });
      volgend = metTekstlaag(volgend, {
        tekst: 'Productnaam',
        startSeconde: 3,
        duurSeconde: 4,
        y: 0.18,
        fontSize: 60,
        fontWeight: 700,
        kleur: HUISSTIJL.licht,
      });
      volgend = metTekstlaag(volgend, {
        tekst: 'Prijs op aanvraag',
        startSeconde: 3,
        duurSeconde: 4,
        y: 0.88,
        fontSize: 44,
        fontWeight: 600,
        kleur: HUISSTIJL.primair,
      });
      return volgend;
    },
  },
  {
    id: 'projectpresentatie',
    naam: 'Projectpresentatie',
    beschrijving: 'Titel, tussenkop en een afsluitende bedanktkaart. Geschikt voor opleveringen.',
    formaat: '16:9',
    fps: 30,
    bouw: (project) => {
      let volgend = metTekstlaag(project, {
        tekst: 'Projectnaam\nLocatie en datum',
        startSeconde: 0,
        duurSeconde: 4,
        y: 0.5,
        fontSize: 76,
        fontWeight: 800,
        kleur: HUISSTIJL.licht,
        animatie: 'vervagen',
      });
      volgend = metTekstlaag(volgend, {
        tekst: 'Uitgangssituatie',
        startSeconde: 4,
        duurSeconde: 6,
        y: 0.12,
        fontSize: 52,
        fontWeight: 700,
        kleur: HUISSTIJL.licht,
      });
      volgend = metTekstlaag(volgend, {
        tekst: 'Bedankt voor uw vertrouwen',
        startSeconde: 12,
        duurSeconde: 4,
        y: 0.5,
        fontSize: 64,
        fontWeight: 700,
        kleur: HUISSTIJL.primair,
        animatie: 'vervagen',
      });
      return volgend;
    },
  },
  {
    id: 'verticale-advertentie',
    naam: 'Verticale socialmedia-advertentie',
    beschrijving: 'Korte, krachtige tekstlagen in 9:16 met een call-to-action onderaan.',
    formaat: '9:16',
    fps: 30,
    bouw: (project) => {
      let volgend = metTekstlaag(project, {
        tekst: 'Wist je dat?',
        startSeconde: 0,
        duurSeconde: 2.5,
        y: 0.3,
        fontSize: 88,
        fontWeight: 800,
        kleur: HUISSTIJL.licht,
        animatie: 'omhoog',
      });
      volgend = metTekstlaag(volgend, {
        tekst: 'Bespaar tot 30% op je energierekening',
        startSeconde: 2.5,
        duurSeconde: 4,
        y: 0.42,
        fontSize: 64,
        fontWeight: 700,
        kleur: HUISSTIJL.licht,
        animatie: 'vervagen',
      });
      volgend = metTekstlaag(volgend, {
        tekst: 'Bekijk ons assortiment',
        startSeconde: 6.5,
        duurSeconde: 3.5,
        y: 0.86,
        fontSize: 60,
        fontWeight: 800,
        kleur: HUISSTIJL.primair,
        animatie: 'omhoog',
      });
      return volgend;
    },
  },
];

export function TemplatePaneel({ editor }: TemplatePaneelProps) {
  const [fout, setFout] = useState<string | null>(null);
  const [huisstijl, setHuisstijl] = useState(HUISSTIJL);

  const toepassen = (template: TemplateDefinitie) => {
    if (!editor.project) return;
    const formaat = BEELDFORMATEN[template.formaat];
    editor.bewerk((project) => {
      const metFormaat: ProjectModel = {
        ...project,
        beeldformaat: formaat.id,
        breedte: formaat.breedte,
        hoogte: formaat.hoogte,
        fps: template.fps,
      };
      return template.bouw(metFormaat);
    }, `Template: ${template.naam}`);
    setFout(
      `Template "${template.naam}" toegepast. De tekstlagen staan klaar; vul ze aan en sleep je eigen clips op de videosporen.`,
    );
  };

  return (
    <div className="templatepaneel">
      <h3>Templates</h3>
      <p className="paneel__hint">
        Een template voegt vaste tekstlagen toe. Media voeg je daarna zelf toe, zodat er nooit naar
        een mediabestand van iemand anders wordt verwezen.
      </p>

      <ul className="templatelijst">
        {TEMPLATES.map((template) => (
          <li key={template.id}>
            <button type="button" className="templatekaart" onClick={() => toepassen(template)}>
              <span className={`templatekaart__vlak templatekaart__vlak--${template.formaat.replace(':', '-')}`} />
              <strong>{template.naam}</strong>
              <span>{template.beschrijving}</span>
              <span className="templatekaart__meta">
                {BEELDFORMATEN[template.formaat].breedte}×{BEELDFORMATEN[template.formaat].hoogte} ·{' '}
                {template.fps} fps
              </span>
            </button>
          </li>
        ))}
      </ul>

      <h3>Huisstijl</h3>
      <p className="paneel__hint">
        Leg je kleuren vast, dan gebruiken de templates en nieuwe tekstlagen die automatisch.
      </p>
      <div className="huisstijl">
        {(
          [
            { sleutel: 'primair' as const, label: 'Accentkleur' },
            { sleutel: 'licht' as const, label: 'Tekstkleur' },
            { sleutel: 'donker' as const, label: 'Achtergrond' },
          ]
        ).map((item) => (
          <label key={item.sleutel} className="huisstijl__veld">
            <span>{item.label}</span>
            <input
              type="color"
              value={huisstijl[item.sleutel]}
              onChange={(e) => {
                const waarde = e.target.value;
                HUISSTIJL[item.sleutel] = waarde;
                setHuisstijl({ ...HUISSTIJL });
              }}
            />
            <code>{huisstijl[item.sleutel]}</code>
          </label>
        ))}
      </div>

      {fout && <div className="melding melding--info melding--klein">{fout}</div>}

      <div className="templatepaneel__acties">
        <button
          type="button"
          className="knop knop--breed"
          onClick={() => {
            if (!editor.project) return;
            const naam = window.prompt('Naam voor dit sjabloon:', `${editor.project.naam} als template`);
            if (!naam) return;
            // Het huidige project opslaan als blauwdruk, zonder media.
            const blauwdruk: ProjectModel = {
              ...editor.project,
              naam,
              tracks: editor.project.tracks.map((track) => ({
                ...track,
                items: track.items.filter((i) => i.soort === 'tekst'),
              })),
            };
            editor.overschrijf(blauwdruk, `Template: ${naam}`);
            setFout(
              'De tekstlagen van dit project zijn als blauwdruk gezet. Media zijn bewust niet meegenomen.',
            );
          }}
        >
          Huidige tekstlagen bewaren als blauwdruk
        </button>
      </div>

      <p className="paneel__hint">
        Duur van de huidige montage: {(berekenDuurInFrames(editor.project!) / editor.project!.fps).toFixed(2)} s
      </p>
    </div>
  );
}
